import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { ShimmerBox } from '../components/Shimmer';
import '../css/Shimmer.css';
import '../css/ServiceDetails.css';
import { BaseLocation } from '../assets';
import pauseConfirmIcon from '../assets/images/Formicons/pause_confirm.svg';
import {
  getBaseLocationDetail,
  toggleBaseService,
  getAllServices,
  clearError,
} from '../features/serviceLocations/serviceLocationSlice';



const ChevronRightNav = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
    <path d="M7.5 5L12.5 10L7.5 15" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);



const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M3 3L13 13M13 3L3 13" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// ─── Toggle Confirm Modal ─────────────────────────────────────────────────
const ToggleConfirmModal = ({ service, onConfirm, onCancel, isLoading }) => {
  const willEnable = !service.IsEnabled;

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [onCancel]);

  return (
    <div className="sd-modal-overlay" onClick={onCancel}>
      <div className="sd-confirm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sd-confirm-header">
          <button className="sd-confirm-close" onClick={onCancel}><CloseIcon /></button>
        </div>
        <div className="sd-confirm-body">
          <div className="sd-confirm-icon">
            <img src={pauseConfirmIcon} alt="" style={{ width: 80, height: 80 }} />
          </div>
          <h4 className="sd-confirm-title">{willEnable ? 'Enable Service' : 'Disable Service'}</h4>
          <p className="sd-confirm-message">
            Are you sure you want to <strong>{willEnable ? 'enable' : 'disable'}</strong>{' '}
            <strong>{service.ServiceName}</strong> at this location?
          </p>
          {!willEnable && (
            <p className="sd-confirm-sub">Customers will not be able to book this service until it is re-enabled.</p>
          )}
        </div>
        <div className="sd-confirm-footer">
          <button className="sd-confirm-btn sd-confirm-btn--outline" onClick={onCancel} disabled={isLoading}>CANCEL</button>
          <button
            className={`sd-confirm-btn ${willEnable ? 'sd-confirm-btn--enable' : 'sd-confirm-btn--disable'}`}
            onClick={onConfirm} disabled={isLoading}
          >
            {isLoading ? '...' : willEnable ? 'ENABLE' : 'DISABLE'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Add Service Panel ────────────────────────────────────────────────────
const AddServicePanel = ({ base, allServices, isLoadingServices, enablingServiceId, onEnable,onDisable, onClose }) => {
  const enabledIds = new Set((base?.Services || []).map((s) => s.ServiceId));

  return (
    <div className="sd-add-panel">
      <div className="sd-add-panel-header">
        <div>
          <h3 className="sd-add-panel-title">Enable a New Service</h3>
          <p className="sd-add-panel-sub">
            Settings are auto-cloned from existing locations. Content (description, images, highlights) is shared globally.
          </p>
        </div>
        <button className="sd-add-panel-close" onClick={onClose}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M3 3L13 13M13 3L3 13" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <div className="sd-add-panel-grid">
        {isLoadingServices ? (
          [1, 2, 3, 4].map((i) => (
            <div key={i} className="sd-add-service-item">
              <ShimmerBox width="44px" height="44px" borderRadius="10px" />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <ShimmerBox width="120px" height="15px" borderRadius="6px" />
                <ShimmerBox width="70px" height="12px" borderRadius="6px" />
              </div>
              <ShimmerBox width="68px" height="34px" borderRadius="8px" />
            </div>
          ))
        ) : allServices.length === 0 ? (
          <div style={{ gridColumn: '1/-1', padding: '24px', textAlign: 'center', color: '#94a3b8', fontFamily: '"DM Sans"', fontSize: 14 }}>
            No services available to add.
          </div>
        ) : (
          allServices.map((svc) => {
            const isEnabled  = enabledIds.has(svc.ServiceId);
            const isEnabling = enablingServiceId === svc.ServiceId;

            return (
              <div key={svc.ServiceId} className={`sd-add-service-item ${isEnabled ? 'sd-add-service-item--enabled' : ''}`}>
                <div className="sd-add-service-icon">
                  {svc.Icon
                    ? <img src={svc.Icon} alt={svc.ServiceName} style={{ width: 28, height: 28, objectFit: 'contain' }} />
                    : (
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="3" width="18" height="18" rx="4" fill="#6366F1" opacity="0.15" />
                        <path d="M8 12h8M12 8v8" stroke="#6366F1" strokeWidth="1.8" strokeLinecap="round" />
                      </svg>
                    )
                  }
                </div>

                <div className="sd-add-service-info">
                  <span className="sd-add-service-name">{svc.ServiceName}</span>
                  <span className="sd-add-service-variants">
                    {svc.SubServicesCount ?? svc.VariantCount ?? 0} Variant{(svc.SubServicesCount ?? svc.VariantCount ?? 0) !== 1 ? 's' : ''}
                  </span>
                </div>

                <button
  className={isEnabled ? 'sd-add-service-disable-btn' : 'sd-add-service-enable-btn'}
  onClick={() => isEnabled ? onDisable(svc) : onEnable(svc)}
  disabled={isEnabling}
>
  {isEnabling ? '…' : isEnabled ? 'Disable' : 'Enable'}
</button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// ─── Sub-service pill ─────────────────────────────────────────────────────
const SubServicePill = ({ sub }) => (
  <div className={`sd-sub-pill${sub.IsEnabled ? ' sd-sub-pill--on' : ' sd-sub-pill--off'}`}>
    <div className="sd-sub-pill-top">
      <span className="sd-sub-pill-name">{sub.SubServiceName}</span>
      <span className="sd-sub-pill-price">{sub.PriceDisplay}</span>
    </div>
    {sub.Description && <span className="sd-sub-pill-desc">{sub.Description}</span>}
    <span className={`sd-sub-status-dot${sub.IsEnabled ? ' sd-sub-status-dot--on' : ''}`} />
  </div>
);

// ─── Service Card ─────────────────────────────────────────────────────────
const ServiceCard = ({ service, index, baseId, onToggleClick, onNavigate }) => {
  const enabledCount = service.EnabledSubCount ?? 0;
  const totalCount   = service.TotalSubCount   ?? 0;

  return (
    <div className="sd-service-card" style={{ animationDelay: `${index * 60}ms` }}>
      <div className="sd-service-card-top">
        <div className="sd-service-left">
          <div className="sd-service-icon-wrap">
            {service.Icon ? (
              <img src={service.Icon} alt={service.ServiceName} className="sd-service-icon-img" />
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="18" height="18" rx="4" fill="#6366F1" opacity="0.12" />
                <path d="M8 12h8M12 8v8" stroke="#6366F1" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            )}
          </div>
          <div className="sd-service-info">
            <h3 className="sd-service-name">{service.ServiceName}</h3>
            {service.Description && <p className="sd-service-desc">{service.Description}</p>}
            <div className="sd-service-meta">
              <span className="sd-meta-chip">
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                  <circle cx="7" cy="7" r="6" stroke="#6366F1" strokeWidth="1.4" />
                  <path d="M7 4v3.5l2 2" stroke="#6366F1" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {service.RadiusMiles} mi / {service.RadiusKm} km
              </span>
              <span className="sd-meta-chip">
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                  <path d="M2 4h10M2 7h6M2 10h8" stroke="#6366F1" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
                {totalCount} variant{totalCount !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>

        <div className="sd-service-right">
          <div className="sd-service-right-actions">
            {/* <button
              className={`sd-toggle-btn${service.IsEnabled ? ' sd-toggle-btn--disable' : ' sd-toggle-btn--enable'}`}
              onClick={() => onToggleClick(service)}
            >
              {service.IsEnabled ? 'Disable' : 'Enable'}
            </button> */}
            <button className="sd-nav-arrow-btn" onClick={() => onNavigate(service)} title="Service Settings">
              <ChevronRightNav />
            </button>
          </div>
          <span className="sd-sub-count">{enabledCount}/{totalCount} active</span>
        </div>
      </div>
    </div>
  );
};

// ─── Shimmer skeleton — matches actual layout exactly ────────────────────
const DetailSkeleton = () => (
  <>
    {/* Location header card shimmer */}
    <div className="sd-location-header-card" style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1 }}>
        {/* Icon */}
        <ShimmerBox width="48px" height="48px" borderRadius="12px" />
        {/* Name + address */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
          <ShimmerBox width="180px" height="20px" borderRadius="6px" />
          <ShimmerBox width="260px" height="14px" borderRadius="6px" />
        </div>
      </div>
      {/* Right chips */}
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        <ShimmerBox width="90px" height="32px" borderRadius="8px" />
        <ShimmerBox width="90px" height="32px" borderRadius="8px" />
        <ShimmerBox width="110px" height="32px" borderRadius="8px" />
      </div>
    </div>

    {/* Services title row shimmer */}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
      <ShimmerBox width="80px" height="18px" borderRadius="6px" />
      <ShimmerBox width="60px" height="14px" borderRadius="6px" />
    </div>

    {/* Service card shimmers — match sd-service-card layout */}
    {[1, 2, 3].map((i) => (
      <div key={i} className="sd-service-card" style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          {/* Left: icon + info */}
          <div style={{ display: 'flex', gap: 14, flex: 1 }}>
            <ShimmerBox width="48px" height="48px" borderRadius="10px" />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <ShimmerBox width="160px" height="17px" borderRadius="6px" />
              <ShimmerBox width="240px" height="13px" borderRadius="6px" />
              <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                <ShimmerBox width="90px" height="26px" borderRadius="8px" />
                <ShimmerBox width="80px" height="26px" borderRadius="8px" />
              </div>
            </div>
          </div>
          {/* Right: toggle + arrow */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <ShimmerBox width="72px" height="32px" borderRadius="8px" />
              <ShimmerBox width="34px" height="34px" borderRadius="8px" />
            </div>
            <ShimmerBox width="60px" height="12px" borderRadius="6px" />
          </div>
        </div>
      </div>
    ))}
  </>
);

// ─── Main Screen ──────────────────────────────────────────────────────────
const ServiceDetailsScreen = () => {
  const { baseId } = useParams();
  const dispatch   = useDispatch();
  const navigate   = useNavigate();

  const {
    currentBase,
    isLoadingDetail,
    isTogglingService,
    allServices,
    isLoadingServices,
    error,
  } = useSelector((state) => state.serviceLocations);


  const [confirmToggle,     setConfirmToggle]     = useState(null);
  const [showAddPanel,      setShowAddPanel]       = useState(false);
  const [enablingServiceId, setEnablingServiceId]  = useState(null);
  const [toast,             setToast]              = useState(null);

  useEffect(() => {
    if (baseId) dispatch(getBaseLocationDetail(baseId));
  }, [baseId, dispatch]);

  useEffect(() => {
    if (error) { showToast('error', error); dispatch(clearError()); }
  }, [error, dispatch]);

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const handleOpenAddPanel = () => {
    setShowAddPanel(true);
    if (allServices.length === 0) dispatch(getAllServices());
  };

  const handleEnableFromPanel = async (svc) => {
    setEnablingServiceId(svc.ServiceId);
    const result = await dispatch(
      toggleBaseService({ baseId, serviceId: svc.ServiceId, isEnabled: true })
    );
    setEnablingServiceId(null);
    if (toggleBaseService.fulfilled.match(result)) {
      showToast('success', `${svc.ServiceName} enabled at this location.`);
      dispatch(getBaseLocationDetail(baseId));
    }
  };

  const handleDisableFromPanel = async (svc) => {
  setEnablingServiceId(svc.ServiceId);
  const result = await dispatch(
    toggleBaseService({ baseId, serviceId: svc.ServiceId, isEnabled: false })
  );
  setEnablingServiceId(null);
  if (toggleBaseService.fulfilled.match(result)) {
    showToast('success', `${svc.ServiceName} disabled at this location.`);
    dispatch(getBaseLocationDetail(baseId));
  }
};

  const handleToggleClick   = (service) => setConfirmToggle({ service });

  const handleConfirmToggle = async () => {
    if (!confirmToggle) return;
    const { service } = confirmToggle;
    const willEnable  = !service.IsEnabled;
    const result      = await dispatch(
      toggleBaseService({ baseId, serviceId: service.ServiceId, isEnabled: willEnable })
    );
    if (toggleBaseService.fulfilled.match(result)) {
      setConfirmToggle(null);
      showToast('success', `${service.ServiceName} ${willEnable ? 'enabled' : 'disabled'} successfully.`);
      dispatch(getBaseLocationDetail(baseId));
    }
  };

  const handleServiceNavigate = (service) =>
    navigate(`/service-location/${baseId}/service/${service.ServiceId}`);

  const base = currentBase;

  return (
    <div className="sd-page">
      <Container fluid className="px-4 py-4">

        {/* Toast */}
        {toast && (
          <div className={`sd-toast sd-toast--${toast.type}`}>
            {toast.type === 'success' ? (
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <circle cx="9" cy="9" r="8" fill="#10B981" opacity="0.15" stroke="#10B981" strokeWidth="1.5" />
                <path d="M5 9l3 3 5-5" stroke="#10B981" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <circle cx="9" cy="9" r="8" fill="#EF4444" opacity="0.15" stroke="#EF4444" strokeWidth="1.5" />
                <path d="M6 6l6 6M12 6l-6 6" stroke="#EF4444" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            )}
            <span>{toast.msg}</span>
          </div>
        )}

        {isLoadingDetail ? (
          <DetailSkeleton />
        ) : !base ? (
          <div className="sd-error-state">
            <div className="sd-error-icon">⚠️</div>
            <h3>Location not found</h3>
            <p>This base location could not be loaded.</p>
            <button className="sd-back-full-btn" onClick={() => navigate(-1)}>Go Back</button>
          </div>
        ) : (
          <>
            {/* Location Header Card */}
            <div className="sd-location-header-card">
              <div className="sd-location-header-left">
                <div className="sd-location-icon-wrap">
                  <img src={BaseLocation} alt="Base" className="sd-location-icon" />
                </div>
                <div className="sd-location-info">
                  <div className="sd-location-name-row">
                    <h2 className="sd-location-name">{base.BaseName}</h2>
                  </div>
                  <div className="sd-location-address-row">
                    <span className="sd-location-address">
                      {base.Address}
                      {base.City  ? `, ${base.City}`  : ''}
                      {base.State ? `, ${base.State}` : ''}
                    </span>
                  </div>
                </div>
              </div>

              <div className="sd-location-header-right">
                <div className="sd-count-chips">
                  <span className="sd-count-chip sd-count-chip--active">
                    {base.EnabledServicesCount ?? 0} Active
                  </span>
                  <span className="sd-count-chip sd-count-chip--available">
                     {base.AvailableCount ?? 0} Available
                  </span>
                  <button className="sd-add-service-btn" onClick={handleOpenAddPanel}>
                    Add Service
                  </button>
                </div>
              </div>
            </div>

            {/* Add Service Panel */}
            {showAddPanel && (
             <AddServicePanel
  base={base}
  allServices={allServices}
  isLoadingServices={isLoadingServices}
  enablingServiceId={enablingServiceId}
  onEnable={handleEnableFromPanel}
  onDisable={handleDisableFromPanel}
  onClose={() => setShowAddPanel(false)}
/>
            )}

            {/* Services */}
            <div className="sd-services-section">
              <div className="sd-services-header">
                <h3 className="sd-services-title">Services</h3>
                <span className="sd-services-count">
                  {base.Services?.length ?? 0} service{base.Services?.length !== 1 ? 's' : ''}
                </span>
              </div>

              {!base.Services?.length ? (
                <div className="sd-no-services">
                  <div className="sd-no-services-icon">🔧</div>
                  <h4>No services assigned</h4>
                  <p>Add services to this location to get started.</p>
                </div>
              ) : (
                <div className="sd-services-list">
                  {base.Services.map((svc, idx) => (
                    <ServiceCard
                      key={svc.ServiceId}
                      service={svc}
                      index={idx}
                      baseId={baseId}
                      onToggleClick={handleToggleClick}
                      onNavigate={handleServiceNavigate}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </Container>

      {confirmToggle && (
        <ToggleConfirmModal
          service={confirmToggle.service}
          onConfirm={handleConfirmToggle}
          onCancel={() => setConfirmToggle(null)}
          isLoading={isTogglingService}
        />
      )}
    </div>
  );
};

export default ServiceDetailsScreen;